import { motion, AnimatePresence } from "motion/react";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  previewContent: string[];
  instructor: string;
  duration: string;
  curriculum: { chapter: string; title: string; duration: string }[];
}

export default function PreviewModal({
  isOpen,
  onClose,
  title,
  previewContent,
  instructor,
  duration,
  curriculum,
}: PreviewModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative w-full max-w-[560px] mx-4 max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden z-10 flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#1C1917] px-6 py-5 flex-shrink-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <i className="ri-eye-line text-[#C4956A]"></i>
                  <span className="text-xs font-medium text-[#C4956A]">3분 미리보기</span>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                  aria-label="닫기"
                >
                  <i className="ri-close-line text-white"></i>
                </button>
              </div>
              <h2 className="text-base font-bold text-white leading-snug">{title}</h2>
              <p className="text-xs text-white/50 mt-1">
                {instructor} · 총 {duration}
              </p>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6 md:p-8 space-y-6">
              {/* Preview Content */}
              <div>
                <h3 className="text-sm font-bold text-[#1C1917] mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-[#C4956A]"></i>
                  이 강의에서 배울 내용
                </h3>
                <div className="space-y-2">
                  {previewContent.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-[#FAFAF8] border border-[#F0EDE8]"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[11px] font-bold text-[#C4956A]">{idx + 1}</span>
                      </div>
                      <p className="text-sm text-[#5C5650] leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Curriculum Preview */}
              <div>
                <h3 className="text-sm font-bold text-[#1C1917] mb-3 flex items-center gap-2">
                  <i className="ri-list-check-2 text-[#C4956A]"></i>
                  커리큘럼 미리보기
                </h3>
                <div className="space-y-1.5">
                  {curriculum.slice(0, 4).map((item) => (
                    <div
                      key={item.chapter}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FAFAF8] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-[#C4956A]">{item.chapter}</span>
                        </div>
                        <span className="text-sm text-[#5C5650]">{item.title}</span>
                      </div>
                      <span className="text-xs text-[#8C8780]">{item.duration}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-[#F0EDE8] flex-shrink-0 bg-[#FAFAF8]">
              <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#C4956A]/5 border border-[#C4956A]/10">
                <i className="ri-information-line text-xs text-[#C4956A]"></i>
                <p className="text-[11px] text-[#5C5650]">
                  전체 강의를 수강하시면 <strong>핵심 요약 PDF 가이드북</strong>을 무료로 다운로드할 수 있습니다
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full py-3 text-sm font-semibold rounded-full bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
              >
                전체 강의 보러 가기
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}